import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchAdminMerchant, fetchAdminMerchantDashboard } from '../services/adminApi';
import MerchantDashboardPreviewPanel from '../components/MerchantDashboardPreviewPanel';
import AdminMerchantSearchPicker from '../components/AdminMerchantSearchPicker';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

export default function LoyalPassAdminDashboardPreviewPage() {
    const { merchantId } = useParams();
    const navigate = useNavigate();
    const [merchantLabel, setMerchantLabel] = useState(null);
    const [merchantError, setMerchantError] = useState(null);
    const [preview, setPreview] = useState({
        loading: true,
        error: null,
        data: null,
        filter: 'total',
    });

    const loadMerchantHeader = useCallback(async () => {
        if (!merchantId) {
            return;
        }
        setMerchantError(null);
        try {
            const res = await fetchAdminMerchant(merchantId);
            const m = res.data?.merchant;
            setMerchantLabel(m ? `${m.name || 'Merchant'}${m.email ? ` · ${m.email}` : ''}` : null);
        } catch (e) {
            setMerchantLabel(null);
            setMerchantError(extractErrorMessage(e) || 'Failed to load merchant');
        }
    }, [merchantId]);

    const loadDashboard = useCallback(
        async (filter) => {
            if (!merchantId) {
                return;
            }
            setPreview((p) => ({ ...p, loading: true, error: null }));
            try {
                const res = await fetchAdminMerchantDashboard(merchantId, { filter });
                setPreview({
                    loading: false,
                    error: null,
                    data: res.data,
                    filter: res.data?.filter || filter,
                });
            } catch (e) {
                setPreview((p) => ({
                    ...p,
                    loading: false,
                    error: extractErrorMessage(e) || 'Failed to load dashboard',
                    data: null,
                }));
            }
        },
        [merchantId]
    );

    useEffect(() => {
        loadMerchantHeader();
    }, [loadMerchantHeader]);

    useEffect(() => {
        if (!merchantId) {
            return;
        }
        loadDashboard('total');
    }, [merchantId, loadDashboard]);

    const onFilterChange = (filter) => {
        loadDashboard(filter);
    };

    if (!merchantId) {
        return (
            <div className="p-6 max-w-4xl mx-auto">
                <p className="text-slate-600 text-sm">Missing merchant id.</p>
                <Link to="/dashboard/loyalpass-admin/merchants" className="text-primary text-sm mt-2 inline-block">
                    Back to Merchants
                </Link>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <Link
                        to="/dashboard/loyalpass-admin/merchants"
                        className="text-sm text-primary font-medium hover:underline"
                    >
                        ← Merchants
                    </Link>
                    <h1 className="text-2xl font-semibold text-slate-900 mt-2">Dashboard preview</h1>
                    {merchantError && <p className="text-sm text-red-600 mt-1">{merchantError}</p>}
                    {!merchantError && merchantLabel && (
                        <p className="text-slate-600 mt-1 text-sm">{merchantLabel}</p>
                    )}
                    {!merchantError && !merchantLabel && (
                        <p className="text-slate-500 mt-1 text-sm font-mono break-all">{merchantId}</p>
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 mb-6">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Switch merchant
                </h2>
                <AdminMerchantSearchPicker
                    autoLoadRecent
                    selectedMerchantId={merchantId}
                    onSelectMerchant={(id) => {
                        if (String(id) === String(merchantId)) {
                            return;
                        }
                        navigate(`/dashboard/loyalpass-admin/dashboard-previews/${id}`, { replace: true });
                    }}
                />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-800 mb-4">Overview metrics</h2>
                <MerchantDashboardPreviewPanel previewState={preview} onFilterChange={onFilterChange} />
            </div>
        </div>
    );
}
